// Part 1: Component Setup and State Definitions
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { formatDateTime } from "../../../utils/dateFormat";
import NewInvoice from "./NewInvoice";
import { bulkDeleteInvoices } from "../../../services/invoice.services";
import { deleteInvoices } from "../../../state/reducers/invoiceSlice";

function InvoiceTable({
  customerId,
  getAllInvoices,
  viewInvoice,
  setViewInvoice,
}) {
  const invoices = useSelector((state) => state.invoice.invoices);
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.user.role);
  // Local states
  const [serviceFilter, setServiceFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "issue_date",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [searchActive, setSearchActive] = useState(false);
  const [mobileView, setMobileView] = useState(window.innerWidth < 768);
  const itemsPerPage = 10;

  // Monitor screen size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleNewInvoice = () => {
    setIsCreatingInvoice(true);
  };

  const handleCloseInvoice = () => {
    setIsCreatingInvoice(false);
  };

  const statusCounts = useMemo(() => {
    const filtered = customerId
      ? invoices.filter(
          (invoice) => invoice.customer?.id === parseInt(customerId)
        )
      : invoices;

    return {
      All: filtered.length,
      Pending: filtered.filter((i) => i.status === "Pending").length,
      Paid: filtered.filter((i) => i.status === "Paid").length,
      Overdue: filtered.filter((i) => i.status === "Overdue").length,
      Draft: filtered.filter((i) => i.status === "Draft").length,
      "Partially Paid": filtered.filter((i) => i.status === "Partially Paid")
        .length,
    };
  }, [invoices, customerId]);

  const handleTabChange = (status) => {
    setActiveTab(status);
    setCurrentPage(1); // Reset to the first page when changing tabs
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 1000); // Simulate loading delay
  }, []);

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) {
      alert("Please select invoices to delete.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedInvoices.length} invoice(s)?`
    );

    if (confirmed) {
      try {
        await bulkDeleteInvoices(selectedInvoices);
        dispatch(deleteInvoices(selectedInvoices));
        setSelectedInvoices([]);
        alert(`${selectedInvoices.length} invoice(s) deleted successfully.`);
      } catch (error) {
        console.error("Failed to delete invoices:", error);
        alert("An error occurred while deleting invoices.");
      }
    }
  };

  const sortInvoices = (invoices) => {
    if (!sortConfig.key) return invoices;
    return [...invoices].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties like "customer.name"
      if (sortConfig.key.includes(".")) {
        const keys = sortConfig.key.split(".");
        aValue = keys.reduce((obj, key) => obj?.[key] ?? "", a);
        bValue = keys.reduce((obj, key) => obj?.[key] ?? "", b);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      // Handle date comparison
      if (sortConfig.key === "issue_date" || sortConfig.key === "due_date") {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      }

      // Handle numeric comparison
      if (sortConfig.key === "total_amount") {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      const order = sortConfig.direction === "asc" ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  }; // Part 2: Filtering, Pagination and Utility Functions

  const filteredInvoices = sortInvoices(
    invoices.filter((invoice) => {
      const issueDate = new Date(invoice.issue_date);
      const dueDate = new Date(invoice.due_date);
      const startDate = startDateFilter ? new Date(startDateFilter) : null;
      const endDate = endDateFilter ? new Date(endDateFilter) : null;

      const matchesCustomer =
        !customerId || invoice.customer?.id === parseInt(customerId);
      const matchesService =
        !serviceFilter || invoice.service === serviceFilter;
      const matchesStartDate = !startDate || issueDate >= startDate;
      const matchesEndDate = !endDate || dueDate <= endDate;
      const matchesSearch =
        !searchFilter ||
        invoice.customer?.name
          ?.toLowerCase()
          .includes(searchFilter.toLowerCase()) ||
        invoice.invoice_number
          .toLowerCase()
          .includes(searchFilter.toLowerCase());
      const matchesStatus = activeTab === "All" || invoice.status === activeTab;

      return (
        matchesCustomer &&
        matchesService &&
        matchesStartDate &&
        matchesEndDate &&
        matchesSearch &&
        matchesStatus
      );
    })
  );

  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAll = () => {
    const allIds = paginatedInvoices.map((invoice) => invoice.id);
    setSelectedInvoices((prev) =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleBulkExport = async () => {
    if (selectedInvoices.length === 0) {
      alert("Please select invoices to export.");
      return;
    }

    try {
      const invoicesToExport = invoices.filter((invoice) =>
        selectedInvoices.includes(invoice.id)
      );

      const headers = [
        "Invoice Number",
        "Customer",
        "Issue Date",
        "Due Date",
        "Total Amount",
        "Status",
        "Invoice Link",
        "",
      ];

      let csvContent = headers.join(",") + "\n";

      invoicesToExport.forEach((invoice) => {
        const lastPayment = invoice.payments?.[invoice.payments.length - 1];
        const transactionReference = lastPayment?.transaction_reference || "";

        const invoiceUrl = transactionReference
          ? `${process.env.REACT_APP_BASE_URL}/media/invoices/${transactionReference}.pdf`
          : "";

        const row = [
          `"${invoice.invoice_number}"`,
          `"${invoice.customer?.name || "Unknown Customer"}"`,
          `"${formatDateTime(invoice.issue_date)}"`,
          `"${formatDateTime(invoice.due_date)}"`,
          `"${invoice.total_amount}"`,
          `"${invoice.status}"`,
          invoiceUrl ? invoiceUrl : "",
          invoiceUrl ? `"View Invoice)"` : "",
        ];
        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `invoices_export_${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSelectedInvoices([]);
    } catch (error) {
      console.error("Failed to export invoices:", error);
      alert("An error occurred while exporting invoices.");
    }
  };

  const handleViewInvoice = (invoice) => {
    setViewInvoice(invoice);
  };

  const handleBackToList = () => {
    setViewInvoice(null);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 opacity-30"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }

    if (sortConfig.direction === "asc") {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-indigo-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-indigo-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800 border-green-200";
      case "Pending":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Partially Paid":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "Draft":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getInitialBackground = (name) => {
    const colors = [
      "bg-indigo-500",
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-red-500",
      "bg-yellow-500",
      "bg-teal-500",
    ];

    let hash = 0;
    if (!name) return colors[0];

    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    hash = Math.abs(hash) % colors.length;
    return colors[hash];
  }; // Part 3: Mobile Card View and Component Render Start

  // Mobile card view for invoices
  const renderMobileInvoiceCard = (invoice) => (
    <div
      key={invoice.id}
      className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden"
      onClick={() => handleViewInvoice(invoice)}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div
              className={`flex-shrink-0 h-10 w-10 rounded-full ${getInitialBackground(
                invoice.customer?.name
              )} flex items-center justify-center text-white text-sm font-medium`}
            >
              {invoice.customer?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900 line-clamp-1">
                {invoice.customer?.name || "Unknown Customer"}
              </div>
              <div className="text-xs text-gray-500">
                {invoice.invoice_number}
              </div>
            </div>
          </div>
          <div
            className="flex items-center"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectInvoice(invoice.id);
            }}
          >
            <input
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              checked={selectedInvoices.includes(invoice.id)}
              onChange={() => {}}
            />
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1">
          <div className="text-xs text-gray-500">Issue Date:</div>
          <div className="text-xs text-gray-900">
            {formatDateTime(invoice.issue_date).split(" ")[0]}
          </div>

          <div className="text-xs text-gray-500">Due Date:</div>
          <div className="text-xs text-gray-900">
            {formatDateTime(invoice.due_date).split(" ")[0]}
          </div>

          <div className="text-xs text-gray-500">Amount:</div>
          <div className="text-xs font-medium text-gray-900">
            ₹{parseFloat(invoice.total_amount).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
        <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(
            invoice.status
          )}`}
        >
          {invoice.status}
        </span>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewInvoice(invoice);
          }}
          className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          View Details
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="ml-1 h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {viewInvoice ? (
        <NewInvoice
          handleCloseInvoice={handleBackToList}
          setViewInvoice={setViewInvoice}
          invoice={viewInvoice}
          isEditingInvoice={true}
          getAllInvoices={getAllInvoices}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Filters and Actions Row */}
          <div className="p-4 border-b border-gray-200 bg-white flex flex-wrap justify-between items-center gap-4">
            {/* Search */}
            <div className="relative w-full sm:w-auto">
              <div
                className={`flex items-center bg-gray-50 rounded-lg ${
                  searchActive ? "ring-2 ring-indigo-300" : ""
                }`}
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search invoices..."
                  className="py-2 pl-10 pr-3 block w-full rounded-lg border-0 bg-gray-50 text-gray-900 focus:ring-0 sm:text-sm"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  onFocus={() => setSearchActive(true)}
                  onBlur={() => setSearchActive(false)}
                />
              </div>
            </div>

            {/* Date Filters - Collapsed on mobile, visible on tablet+ */}
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-gray-500">From:</span>
              <input
                type="date"
                className="p-1.5 rounded border border-gray-300 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
              />
              <span className="text-gray-500">To:</span>
              <input
                type="date"
                className="p-1.5 rounded border border-gray-300 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
              />
            </div>

            {/* Mobile-friendly date filters toggle */}
            <div className="w-full md:hidden">
              <details className="group mb-4">
                <summary className="flex justify-between items-center text-sm font-medium text-gray-600 cursor-pointer">
                  <span>Date Filters</span>
                  <span className="transition group-open:rotate-180">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-xs text-gray-500 block mb-1">
                      From:
                    </span>
                    <input
                      type="date"
                      className="p-1.5 rounded border border-gray-300 text-sm focus:ring-indigo-500 focus:border-indigo-500 w-full"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block mb-1">
                      To:
                    </span>
                    <input
                      type="date"
                      className="p-1.5 rounded border border-gray-300 text-sm focus:ring-indigo-500 focus:border-indigo-500 w-full"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                    />
                  </div>
                </div>
              </details>
            </div>

            <div className="flex justify-between items-center w-full">
              <div className="text-sm text-gray-500">
                {filteredInvoices.length}{" "}
                {filteredInvoices.length === 1 ? "invoice" : "invoices"} found
              </div>
              <div className="flex gap-5">
                <button
                  onClick={() => {
                    const allFilteredIds = filteredInvoices.map(
                      (invoice) => invoice.id
                    );
                    setSelectedInvoices(allFilteredIds);
                    setTimeout(() => handleBulkExport(), 0);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1 text-gray-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Export All
                </button>
                {userRole === "superadmin" && (
                  <button
                    onClick={handleBulkDelete}
                    className="flex-1 sm:flex-initial flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000 2h1v11a2 2 0 002 2h8a2 2 0 002-2V6h1a1 1 0 100-2h-2V3a1 1 0 00-1-1H6zm2 4a1 1 0 012 0v8a1 1 0 11-2 0V6zm4 0a1 1 0 012 0v8a1 1 0 11-2 0V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto scrollbar-hide flex-wrap">
              {[
                "All",
                "Pending",
                "Partially Paid",
                "Paid",
                "Overdue",
                "Draft",
              ].map((status) => (
                <button
                  key={status}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                    activeTab === status
                      ? "text-indigo-600 border-b-2 border-indigo-500 bg-white"
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                  }`}
                  onClick={() => handleTabChange(status)}
                >
                  {status}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      activeTab === status
                        ? "bg-indigo-100 text-indigo-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {statusCounts[status]}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {/* Table for desktop view / Cards for mobile view */}
          {loading ? (
            <div className="px-4 py-8">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            </div>
          ) : paginatedInvoices.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No invoices found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchFilter
                    ? "Try adjusting your search or filter criteria"
                    : "Get started by creating a new invoice"}
                </p>
              </div>
            </div>
          ) : mobileView ? (
            // Mobile card view
            <div className="p-4 space-y-4">
              {paginatedInvoices.map(renderMobileInvoiceCard)}
            </div>
          ) : (
            // Desktop table view
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          onChange={handleSelectAll}
                          checked={
                            paginatedInvoices.length > 0 &&
                            selectedInvoices.length === paginatedInvoices.length
                          }
                        />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("customer.name")}
                    >
                      <div className="flex items-center gap-1">
                        Customer
                        {getSortIcon("customer.name")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("issue_date")}
                    >
                      <div className="flex items-center gap-1">
                        Create Date
                        {getSortIcon("issue_date")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("due_date")}
                    >
                      <div className="flex items-center gap-1">
                        Due Date
                        {getSortIcon("due_date")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("total_amount")}
                    >
                      <div className="flex items-center gap-1">
                        Amount
                        {getSortIcon("total_amount")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {getSortIcon("status")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => handleViewInvoice(invoice)}
                    >
                      <td
                        className="px-4 py-4 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          checked={selectedInvoices.includes(invoice.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectInvoice(invoice.id);
                          }}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className={`flex-shrink-0 h-10 w-10 rounded-full ${getInitialBackground(
                              invoice.customer?.name
                            )} flex items-center justify-center text-white text-sm font-medium`}
                          >
                            {invoice.customer?.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.customer?.name || "Unknown Customer"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {invoice.invoice_number}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(invoice.issue_date).split(" ")[0]}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDateTime(invoice.issue_date).split(" ")[1]}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(invoice.due_date).split(" ")[0]}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDateTime(invoice.due_date).split(" ")[1]}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{parseFloat(invoice.total_amount).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(
                            invoice.status
                          )}`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination - Responsive for both mobile and desktop */}
          {paginatedInvoices.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              {/* Desktop pagination */}
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        currentPage * itemsPerPage,
                        filteredInvoices.length
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">
                      {filteredInvoices.length}
                    </span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>

                    {/* Responsive page numbers - show fewer on smaller screens */}
                    {Array.from(
                      {
                        length: Math.min(
                          totalPages,
                          window.innerWidth < 640 ? 3 : 5
                        ),
                      },
                      (_, i) => {
                        // Logic to show pages around current page
                        let pageNum;
                        const maxVisible = window.innerWidth < 640 ? 3 : 5;

                        if (totalPages <= maxVisible) {
                          pageNum = i + 1;
                        } else if (currentPage <= Math.ceil(maxVisible / 2)) {
                          pageNum = i + 1;
                        } else if (
                          currentPage >=
                          totalPages - Math.floor(maxVisible / 2)
                        ) {
                          pageNum = totalPages - maxVisible + i + 1;
                        } else {
                          pageNum =
                            currentPage - Math.floor(maxVisible / 2) + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    )}

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>

              {/* Mobile pagination - simpler version */}
              <div className="flex sm:hidden justify-between items-center w-full">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {/* Empty state with action */}
          {/* {!loading && invoices.length === 0 && (
            <div className="text-center py-12 px-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto h-16 w-16 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                No invoices yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first invoice to get started
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleNewInvoice}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  New Invoice
                </button>
              </div>
            </div>
          )} */}
          {/* Bulk action floating bar - optimized for mobile and desktop */}
          {selectedInvoices.length > 0 && (
            <div className="fixed bottom-4 inset-x-0 flex justify-center z-20 pointer-events-none px-4">
              <div className="w-full max-w-xl mx-auto">
                <div className="p-2 sm:p-3 rounded-lg bg-indigo-600 shadow-lg pointer-events-auto">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                    <div className="flex items-center">
                      <span className="flex-shrink-0 p-1 sm:p-2 rounded-lg bg-indigo-800">
                        <svg
                          className="h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </span>
                      <p className="ml-2 sm:ml-3 font-medium text-white truncate">
                        <span className="inline">
                          {selectedInvoices.length} invoice(s) selected
                        </span>
                      </p>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => setSelectedInvoices([])}
                        className="flex-1 sm:flex-initial flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleBulkExport}
                        className="flex-1 sm:flex-initial flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Export
                      </button>
                      {userRole === "superadmin" && (
                        <button
                          onClick={handleBulkDelete}
                          className="flex-1 sm:flex-initial flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000 2h1v11a2 2 0 002 2h8a2 2 0 002-2V6h1a1 1 0 100-2h-2V3a1 1 0 00-1-1H6zm2 4a1 1 0 012 0v8a1 1 0 11-2 0V6zm4 0a1 1 0 012 0v8a1 1 0 11-2 0V6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default InvoiceTable;
